import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Save, Mail as MailIcon } from "lucide-react";
import { me, isAuthed, logout, getContent, updateContent, listMessages } from "@/cms/api";
import { useContent } from "@/cms/ContentContext";
import { Field, ImagePicker } from "@/pages/admin/AdminFields";
import SpacingPanel from "@/pages/admin/SpacingPanel";

const TABS = [
  { id: "channels", label: "Canais" },
  { id: "texts", label: "Textos" },
  { id: "spacing", label: "Espaçamentos" },
  { id: "images", label: "Imagens" },
  { id: "sobre", label: "Sobre" },
  { id: "portfolio", label: "Portfolio" },
  { id: "testemunhos", label: "Testemunhos" },
  { id: "messages", label: "Mensagens" },
];

const Admin = () => {
  const nav = useNavigate();
  const { reload } = useContent();
  const [tab, setTab] = useState("channels");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!isAuthed()) {
      nav("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const u = await me();
        setEmail(u.email);
        const c = await getContent();
        setData(c.data);
      } catch (e) {
        logout();
        nav("/admin/login", { replace: true });
      }
    })();
  }, [nav]);

  useEffect(() => {
    if (tab !== "messages") return;
    listMessages().then(setMessages).catch(() => setMessages([]));
  }, [tab]);

  const patch = (path, value) => {
    setData((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (cur[k] == null) cur[k] = /^\d+$/.test(keys[i + 1]) ? [] : {};
        cur = cur[k];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateContent(data);
      await reload();
      toast.success("Alterações guardadas.");
    } catch (e) {
      toast.error("Erro a guardar.");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-serif italic text-[#6B6554]">A carregar…</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen py-10 md:py-16 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <Link to="/" className="overline text-[#6B6554] hover:text-[#3A3524] transition-colors">
              ← Ver site
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-[#3A3524] mt-2 tracking-tight">
              CMS <span className="italic text-[#8C714D]">ORDO</span>
            </h1>
            <div className="overline text-[#6B6554] mt-1">{email}</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#3A3524] text-[#F5F2EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#8C714D] transition-colors disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "A guardar…" : "Guardar"}
            </button>
            <button
              data-testid="admin-logout"
              onClick={doLogout}
              className="inline-flex items-center gap-2 border border-[#3A3524]/40 text-[#3A3524] px-4 py-3 text-[11px] tracking-[0.28em] uppercase hover:border-[#3A3524] transition-colors"
            >
              <LogOut size={14} />
              Sair
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-[#3A3524]/12 mb-10 -mx-2 px-2">
          {TABS.map((tabItem) => (
            <button
              key={tabItem.id}
              data-testid={`admin-tab-${tabItem.id}`}
              onClick={() => setTab(tabItem.id)}
              className={`relative px-4 py-3 text-[11px] tracking-[0.2em] uppercase transition-colors ${
                tab === tabItem.id ? "text-[#3A3524]" : "text-[#6B6554] hover:text-[#3A3524]"
              }`}
            >
              {tabItem.label}
              {tab === tabItem.id && (
                <motion.span
                  layoutId="admin-tab-underline"
                  className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#3A3524]"
                />
              )}
            </button>
          ))}
        </div>

        {/* Panels */}
        {tab === "channels" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            <Field label="Email" value={data.channels?.email} onChange={(v) => patch("channels.email", v)} testid="field-email" />
            <Field label="WhatsApp" value={data.channels?.whatsapp} onChange={(v) => patch("channels.whatsapp", v)} testid="field-whatsapp" placeholder="+351 …" />
            <Field label="Instagram (URL)" value={data.channels?.instagram_url} onChange={(v) => patch("channels.instagram_url", v)} testid="field-instagram" />
            <Field label="Facebook (URL)" value={data.channels?.facebook_url} onChange={(v) => patch("channels.facebook_url", v)} testid="field-facebook" />
          </section>
        )}

        {tab === "texts" && (
          <section className="flex flex-col gap-14">
            <div className="max-w-3xl">
              <div className="font-serif italic text-[#6B6554] text-lg leading-relaxed">
                Editar qualquer texto do site. Deixa em branco para usar o texto original.
                Cada campo tem versão <em>PT</em> e <em>EN</em>.
              </div>
            </div>

            {/* HERO */}
            <div className="flex flex-col gap-6">
              <div className="overline text-[#8C714D]">01 · Hero (topo)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Subtítulo pequeno · PT" value={data.texts?.hero?.overline_pt} onChange={(v) => patch("texts.hero.overline_pt", v)} testid="tx-hero-overline-pt" placeholder="Personal Organizer · Algarve" />
                <Field label="Overline · EN" value={data.texts?.hero?.overline_en} onChange={(v) => patch("texts.hero.overline_en", v)} testid="tx-hero-overline-en" placeholder="Personal Organizer · Algarve" />
                <Field label="Linha 1 do título · PT" value={data.texts?.hero?.line1_pt} onChange={(v) => patch("texts.hero.line1_pt", v)} testid="tx-hero-line1-pt" placeholder="Espaços" />
                <Field label="Line 1 · EN" value={data.texts?.hero?.line1_en} onChange={(v) => patch("texts.hero.line1_en", v)} testid="tx-hero-line1-en" placeholder="Spaces" />
                <Field label="Linha 2 (itálico) · PT" value={data.texts?.hero?.line2_pt} onChange={(v) => patch("texts.hero.line2_pt", v)} testid="tx-hero-line2-pt" placeholder="que respiram," />
                <Field label="Line 2 (italic) · EN" value={data.texts?.hero?.line2_en} onChange={(v) => patch("texts.hero.line2_en", v)} testid="tx-hero-line2-en" placeholder="that breathe," />
                <Field label="Linha 3 · PT" value={data.texts?.hero?.line3_pt} onChange={(v) => patch("texts.hero.line3_pt", v)} testid="tx-hero-line3-pt" placeholder="vidas em" />
                <Field label="Line 3 · EN" value={data.texts?.hero?.line3_en} onChange={(v) => patch("texts.hero.line3_en", v)} testid="tx-hero-line3-en" placeholder="lives in" />
                <Field label="Linha 4 (itálico) · PT" value={data.texts?.hero?.line4_pt} onChange={(v) => patch("texts.hero.line4_pt", v)} testid="tx-hero-line4-pt" placeholder="harmonia." />
                <Field label="Line 4 (italic) · EN" value={data.texts?.hero?.line4_en} onChange={(v) => patch("texts.hero.line4_en", v)} testid="tx-hero-line4-en" placeholder="harmony." />
                <Field label="Sub-texto · PT" value={data.texts?.hero?.sub_pt} onChange={(v) => patch("texts.hero.sub_pt", v)} testid="tx-hero-sub-pt" multiline />
                <Field label="Sub-text · EN" value={data.texts?.hero?.sub_en} onChange={(v) => patch("texts.hero.sub_en", v)} testid="tx-hero-sub-en" multiline />
                <Field label="Botão primário · PT" value={data.texts?.hero?.cta_pt} onChange={(v) => patch("texts.hero.cta_pt", v)} testid="tx-hero-cta-pt" placeholder="Agendar" />
                <Field label="Primary button · EN" value={data.texts?.hero?.cta_en} onChange={(v) => patch("texts.hero.cta_en", v)} testid="tx-hero-cta-en" placeholder="Book a consultation" />
                <Field label="Botão secundário · PT" value={data.texts?.hero?.ctaAlt_pt} onChange={(v) => patch("texts.hero.ctaAlt_pt", v)} testid="tx-hero-cta-alt-pt" placeholder="Ver serviços" />
                <Field label="Secondary button · EN" value={data.texts?.hero?.ctaAlt_en} onChange={(v) => patch("texts.hero.ctaAlt_en", v)} testid="tx-hero-cta-alt-en" placeholder="See services" />
              </div>
            </div>

            {/* SOBRE */}
            <div className="flex flex-col gap-6 border-t border-[#3A3524]/12 pt-10">
              <div className="overline text-[#8C714D]">02 · Sobre</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Overline · PT" value={data.texts?.sobre?.overline_pt} onChange={(v) => patch("texts.sobre.overline_pt", v)} testid="tx-sobre-overline-pt" placeholder="Sobre" />
                <Field label="Overline · EN" value={data.texts?.sobre?.overline_en} onChange={(v) => patch("texts.sobre.overline_en", v)} testid="tx-sobre-overline-en" placeholder="About" />
                <Field label="Título · PT" value={data.texts?.sobre?.title_pt} onChange={(v) => patch("texts.sobre.title_pt", v)} testid="tx-sobre-title-pt" placeholder="Sou a Isa —" />
                <Field label="Title · EN" value={data.texts?.sobre?.title_en} onChange={(v) => patch("texts.sobre.title_en", v)} testid="tx-sobre-title-en" placeholder="I'm Isa —" />
                <Field label="Título (itálico) · PT" value={data.texts?.sobre?.titleItalic_pt} onChange={(v) => patch("texts.sobre.titleItalic_pt", v)} testid="tx-sobre-titleitalic-pt" placeholder="e a ordem é a minha linguagem de amor." />
                <Field label="Title (italic) · EN" value={data.texts?.sobre?.titleItalic_en} onChange={(v) => patch("texts.sobre.titleItalic_en", v)} testid="tx-sobre-titleitalic-en" placeholder="and order is my love language." />
              </div>
              <div className="text-[13px] text-[#6B6554] italic">
                Os parágrafos do "Sobre" continuam a ser editados na tab <strong>Sobre</strong>.
              </div>
            </div>

            {/* SERVIÇOS */}
            <div className="flex flex-col gap-6 border-t border-[#3A3524]/12 pt-10">
              <div className="overline text-[#8C714D]">03 · Serviços</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Overline · PT" value={data.texts?.servicos?.overline_pt} onChange={(v) => patch("texts.servicos.overline_pt", v)} testid="tx-servicos-overline-pt" placeholder="Serviços" />
                <Field label="Overline · EN" value={data.texts?.servicos?.overline_en} onChange={(v) => patch("texts.servicos.overline_en", v)} testid="tx-servicos-overline-en" placeholder="Services" />
                <Field label="Título · PT" value={data.texts?.servicos?.title_pt} onChange={(v) => patch("texts.servicos.title_pt", v)} testid="tx-servicos-title-pt" placeholder="Três formas" />
                <Field label="Title · EN" value={data.texts?.servicos?.title_en} onChange={(v) => patch("texts.servicos.title_en", v)} testid="tx-servicos-title-en" placeholder="Three ways" />
                <Field label="Título (itálico) · PT" value={data.texts?.servicos?.titleItalic_pt} onChange={(v) => patch("texts.servicos.titleItalic_pt", v)} testid="tx-servicos-titleitalic-pt" placeholder="de começar de novo." />
                <Field label="Title (italic) · EN" value={data.texts?.servicos?.titleItalic_en} onChange={(v) => patch("texts.servicos.titleItalic_en", v)} testid="tx-servicos-titleitalic-en" placeholder="to start over." />
              </div>

              {[0, 1, 2].map((i) => (
                <div key={i} className="border-t border-[#3A3524]/12 pt-6 mt-2">
                  <div className="font-serif italic text-[#3A3524] text-xl mb-4">
                    Serviço 0{i + 1}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <Field label="Título · PT" value={data.texts?.servicos?.items?.[i]?.title_pt} onChange={(v) => patch(`texts.servicos.items.${i}.title_pt`, v)} testid={`tx-servico-${i}-title-pt`} />
                    <Field label="Title · EN" value={data.texts?.servicos?.items?.[i]?.title_en} onChange={(v) => patch(`texts.servicos.items.${i}.title_en`, v)} testid={`tx-servico-${i}-title-en`} />
                    <Field label="Descrição · PT" value={data.texts?.servicos?.items?.[i]?.desc_pt} onChange={(v) => patch(`texts.servicos.items.${i}.desc_pt`, v)} testid={`tx-servico-${i}-desc-pt`} multiline />
                    <Field label="Description · EN" value={data.texts?.servicos?.items?.[i]?.desc_en} onChange={(v) => patch(`texts.servicos.items.${i}.desc_en`, v)} testid={`tx-servico-${i}-desc-en`} multiline />
                  </div>
                </div>
              ))}
            </div>

            {/* PORTFOLIO */}
            <div className="flex flex-col gap-6 border-t border-[#3A3524]/12 pt-10">
              <div className="overline text-[#8C714D]">04 · Portfolio</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Overline · PT" value={data.texts?.portfolio?.overline_pt} onChange={(v) => patch("texts.portfolio.overline_pt", v)} testid="tx-portfolio-overline-pt" placeholder="Portfolio" />
                <Field label="Overline · EN" value={data.texts?.portfolio?.overline_en} onChange={(v) => patch("texts.portfolio.overline_en", v)} testid="tx-portfolio-overline-en" placeholder="Portfolio" />
                <Field label="Título · PT" value={data.texts?.portfolio?.title_pt} onChange={(v) => patch("texts.portfolio.title_pt", v)} testid="tx-portfolio-title-pt" placeholder="Antes" />
                <Field label="Title · EN" value={data.texts?.portfolio?.title_en} onChange={(v) => patch("texts.portfolio.title_en", v)} testid="tx-portfolio-title-en" placeholder="Before" />
                <Field label="Título (itálico) · PT" value={data.texts?.portfolio?.titleItalic_pt} onChange={(v) => patch("texts.portfolio.titleItalic_pt", v)} testid="tx-portfolio-titleitalic-pt" placeholder="· Depois" />
                <Field label="Title (italic) · EN" value={data.texts?.portfolio?.titleItalic_en} onChange={(v) => patch("texts.portfolio.titleItalic_en", v)} testid="tx-portfolio-titleitalic-en" placeholder="· After" />
                <Field label="Sub-texto · PT" value={data.texts?.portfolio?.sub_pt} onChange={(v) => patch("texts.portfolio.sub_pt", v)} testid="tx-portfolio-sub-pt" multiline />
                <Field label="Sub-text · EN" value={data.texts?.portfolio?.sub_en} onChange={(v) => patch("texts.portfolio.sub_en", v)} testid="tx-portfolio-sub-en" multiline />
              </div>
            </div>

            {/* TESTEMUNHOS */}
            <div className="flex flex-col gap-6 border-t border-[#3A3524]/12 pt-10">
              <div className="overline text-[#8C714D]">05 · Testemunhos</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Overline · PT" value={data.texts?.testemunhos?.overline_pt} onChange={(v) => patch("texts.testemunhos.overline_pt", v)} testid="tx-testemunhos-overline-pt" placeholder="Testemunhos" />
                <Field label="Overline · EN" value={data.texts?.testemunhos?.overline_en} onChange={(v) => patch("texts.testemunhos.overline_en", v)} testid="tx-testemunhos-overline-en" placeholder="Testimonials" />
                <Field label="Título · PT" value={data.texts?.testemunhos?.title_pt} onChange={(v) => patch("texts.testemunhos.title_pt", v)} testid="tx-testemunhos-title-pt" placeholder="O que dizem" />
                <Field label="Title · EN" value={data.texts?.testemunhos?.title_en} onChange={(v) => patch("texts.testemunhos.title_en", v)} testid="tx-testemunhos-title-en" placeholder="What they say" />
                <Field label="Título (itálico) · PT" value={data.texts?.testemunhos?.titleItalic_pt} onChange={(v) => patch("texts.testemunhos.titleItalic_pt", v)} testid="tx-testemunhos-titleitalic-pt" placeholder="quando fecho a porta." />
                <Field label="Title (italic) · EN" value={data.texts?.testemunhos?.titleItalic_en} onChange={(v) => patch("texts.testemunhos.titleItalic_en", v)} testid="tx-testemunhos-titleitalic-en" placeholder="when I close the door." />
              </div>
              <div className="text-[13px] text-[#6B6554] italic">
                As citações dos clientes continuam na tab <strong>Testemunhos</strong>.
              </div>
            </div>

            {/* CONTACTO */}
            <div className="flex flex-col gap-6 border-t border-[#3A3524]/12 pt-10">
              <div className="overline text-[#8C714D]">06 · Contacto</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Overline · PT" value={data.texts?.contacto?.overline_pt} onChange={(v) => patch("texts.contacto.overline_pt", v)} testid="tx-contacto-overline-pt" placeholder="Contacto" />
                <Field label="Overline · EN" value={data.texts?.contacto?.overline_en} onChange={(v) => patch("texts.contacto.overline_en", v)} testid="tx-contacto-overline-en" placeholder="Contact" />
                <Field label="Título · PT" value={data.texts?.contacto?.title_pt} onChange={(v) => patch("texts.contacto.title_pt", v)} testid="tx-contacto-title-pt" placeholder="Vamos criar" />
                <Field label="Title · EN" value={data.texts?.contacto?.title_en} onChange={(v) => patch("texts.contacto.title_en", v)} testid="tx-contacto-title-en" placeholder="Let's create" />
                <Field label="Título (itálico) · PT" value={data.texts?.contacto?.titleItalic_pt} onChange={(v) => patch("texts.contacto.titleItalic_pt", v)} testid="tx-contacto-titleitalic-pt" placeholder="ordem juntos." />
                <Field label="Title (italic) · EN" value={data.texts?.contacto?.titleItalic_en} onChange={(v) => patch("texts.contacto.titleItalic_en", v)} testid="tx-contacto-titleitalic-en" placeholder="order together." />
                <Field label="Sub-texto · PT" value={data.texts?.contacto?.sub_pt} onChange={(v) => patch("texts.contacto.sub_pt", v)} testid="tx-contacto-sub-pt" multiline />
                <Field label="Sub-text · EN" value={data.texts?.contacto?.sub_en} onChange={(v) => patch("texts.contacto.sub_en", v)} testid="tx-contacto-sub-en" multiline />
              </div>
            </div>
          </section>
        )}

        {tab === "spacing" && <SpacingPanel data={data} patch={patch} />}

        {tab === "images" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <ImagePicker label="Logotipo (nav)" value={data.images?.logo} onChange={(v) => patch("images.logo", v)} testid="pic-logo" />
            <ImagePicker label="Hero" value={data.images?.hero} onChange={(v) => patch("images.hero", v)} testid="pic-hero" />
            <ImagePicker label="Sobre (retrato)" value={data.images?.sobre} onChange={(v) => patch("images.sobre", v)} testid="pic-sobre" />
            <ImagePicker label="Serviço 01" value={data.images?.servico_01} onChange={(v) => patch("images.servico_01", v)} testid="pic-servico-01" />
            <ImagePicker label="Serviço 02" value={data.images?.servico_02} onChange={(v) => patch("images.servico_02", v)} testid="pic-servico-02" />
            <ImagePicker label="Serviço 03" value={data.images?.servico_03} onChange={(v) => patch("images.servico_03", v)} testid="pic-servico-03" />
          </section>
        )}

        {tab === "sobre" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            <Field label="Parágrafo 1 · PT" value={data.sobre?.p1_pt} onChange={(v) => patch("sobre.p1_pt", v)} testid="field-sobre-p1-pt" multiline />
            <Field label="Paragraph 1 · EN" value={data.sobre?.p1_en} onChange={(v) => patch("sobre.p1_en", v)} testid="field-sobre-p1-en" multiline />
            <Field label="Parágrafo 2 · PT" value={data.sobre?.p2_pt} onChange={(v) => patch("sobre.p2_pt", v)} testid="field-sobre-p2-pt" multiline />
            <Field label="Paragraph 2 · EN" value={data.sobre?.p2_en} onChange={(v) => patch("sobre.p2_en", v)} testid="field-sobre-p2-en" multiline />

            {[1, 2, 3].map((n) => (
              <div key={n} className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-[#3A3524]/12 pt-6">
                <Field label={`Estatística ${n} · número`} value={data.sobre?.[`stat${n}_n`]} onChange={(v) => patch(`sobre.stat${n}_n`, v)} testid={`field-stat${n}-n`} placeholder="ex: 120+ · 7 · 98%" />
                <Field label={`Estatística ${n} · PT`} value={data.sobre?.[`stat${n}_l_pt`]} onChange={(v) => patch(`sobre.stat${n}_l_pt`, v)} testid={`field-stat${n}-l-pt`} />
                <Field label={`Estatística ${n} · EN`} value={data.sobre?.[`stat${n}_l_en`]} onChange={(v) => patch(`sobre.stat${n}_l_en`, v)} testid={`field-stat${n}-l-en`} />
              </div>
            ))}
          </section>
        )}

        {tab === "portfolio" && (
          <section className="flex flex-col gap-12">
            {(data.portfolio || []).map((c, i) => (
              <div key={i} data-testid={`admin-portfolio-${i}`} className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-[#3A3524]/12 pt-8">
                <div className="md:col-span-2 flex items-center justify-between">
                  <div className="font-serif italic text-2xl text-[#3A3524]">Caso {String(i + 1).padStart(2, "0")}</div>
                  <button
                    onClick={() => {
                      const next = data.portfolio.filter((_, k) => k !== i);
                      patch("portfolio", next);
                    }}
                    className="text-xs text-[#8C714D] hover:text-[#3A3524]"
                  >
                    Remover
                  </button>
                </div>
                <ImagePicker label="Antes" value={c.before} onChange={(v) => patch(`portfolio.${i}.before`, v)} testid={`pic-before-${i}`} />
                <ImagePicker label="Depois" value={c.after} onChange={(v) => patch(`portfolio.${i}.after`, v)} testid={`pic-after-${i}`} />
                <Field label="Legenda · PT" value={c.label_pt} onChange={(v) => patch(`portfolio.${i}.label_pt`, v)} testid={`field-label-pt-${i}`} />
                <Field label="Label · EN" value={c.label_en} onChange={(v) => patch(`portfolio.${i}.label_en`, v)} testid={`field-label-en-${i}`} />
                <Field label="Tag · PT" value={c.tag_pt} onChange={(v) => patch(`portfolio.${i}.tag_pt`, v)} testid={`field-tag-pt-${i}`} />
                <Field label="Tag · EN" value={c.tag_en} onChange={(v) => patch(`portfolio.${i}.tag_en`, v)} testid={`field-tag-en-${i}`} />
              </div>
            ))}
            <button
              data-testid="admin-portfolio-add"
              onClick={() =>
                patch("portfolio", [
                  ...(data.portfolio || []),
                  { before: "", after: "", label_pt: "", label_en: "", tag_pt: "", tag_en: "" },
                ])
              }
              className="self-start inline-flex items-center gap-2 border border-[#3A3524]/40 hover:border-[#3A3524] text-[#3A3524] px-6 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors"
            >
              + Adicionar caso
            </button>
          </section>
        )}

        {tab === "testemunhos" && (
          <section className="flex flex-col gap-10">
            {(data.testemunhos || []).map((it, i) => (
              <div key={i} data-testid={`admin-testemunho-${i}`} className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-[#3A3524]/12 pt-8">
                <div className="md:col-span-2 flex items-center justify-between">
                  <div className="font-serif italic text-2xl text-[#3A3524]">Testemunho {String(i + 1).padStart(2, "0")}</div>
                  <button
                    onClick={() => patch("testemunhos", data.testemunhos.filter((_, k) => k !== i))}
                    className="text-xs text-[#8C714D] hover:text-[#3A3524]"
                  >
                    Remover
                  </button>
                </div>
                <Field label="Citação · PT" value={it.quote_pt} onChange={(v) => patch(`testemunhos.${i}.quote_pt`, v)} testid={`field-quote-pt-${i}`} multiline />
                <Field label="Quote · EN" value={it.quote_en} onChange={(v) => patch(`testemunhos.${i}.quote_en`, v)} testid={`field-quote-en-${i}`} multiline />
                <Field label="Autor" value={it.author} onChange={(v) => patch(`testemunhos.${i}.author`, v)} testid={`field-author-${i}`} />
                <div />
                <Field label="Papel · PT" value={it.role_pt} onChange={(v) => patch(`testemunhos.${i}.role_pt`, v)} testid={`field-role-pt-${i}`} />
                <Field label="Role · EN" value={it.role_en} onChange={(v) => patch(`testemunhos.${i}.role_en`, v)} testid={`field-role-en-${i}`} />
              </div>
            ))}
            <button
              data-testid="admin-testemunho-add"
              onClick={() =>
                patch("testemunhos", [
                  ...(data.testemunhos || []),
                  { quote_pt: "", quote_en: "", author: "", role_pt: "", role_en: "" },
                ])
              }
              className="self-start inline-flex items-center gap-2 border border-[#3A3524]/40 hover:border-[#3A3524] text-[#3A3524] px-6 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors"
            >
              + Adicionar testemunho
            </button>
          </section>
        )}

        {tab === "messages" && (
          <section data-testid="admin-messages" className="flex flex-col gap-6">
            <div className="overline text-[#6B6554] flex items-center gap-2">
              <MailIcon size={12} /> {messages.length} mensagem(ns) recebida(s)
            </div>
            {messages.length === 0 && (
              <div className="font-serif italic text-[#6B6554]">Ainda não há mensagens.</div>
            )}
            <div className="flex flex-col gap-6">
              {messages.map((m) => (
                <article key={m.id} className="border-t border-[#3A3524]/12 pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-serif text-2xl text-[#3A3524]">{m.name}</div>
                    <div className="overline text-[#6B6554]">
                      {new Date(m.created_at).toLocaleString("pt-PT")}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-[#6B6554] mb-3">
                    <a href={`mailto:${m.email}`} className="hover:text-[#3A3524]">{m.email}</a>
                    {m.phone && <span>· {m.phone}</span>}
                    {m.service && <span>· {m.service}</span>}
                    <span>· {m.language?.toUpperCase()}</span>
                  </div>
                  <p className="text-[#3A3524] leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Admin;
